import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, mensagemDoErro } from '../api';
import { data as fmtData, numero, paraNumero } from '../formato';
import {
  AcoesDoLaudo, Aviso, Campo, Carregando, Dialogo, ErroDaApi, Escolha,
  Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';
import { useSessao } from '../sessao';
import { useCaminho } from '../Caminho';
import { FotosDoRegistro } from '../FotosDoRegistro';
import {
  criarDiario, fecharDiario, lancarParalisacao,
  lancarPontoDoDia, reabrirDiario, verDiario, verDiarios,
  type Diario, type FichaDoDiario,
} from '../api/diarios';
import { verEquipes, type Equipe } from '../api/campo';
import { contarPontos, nomeDoPonto } from '../nomeDoPonto';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../TabelaComFicha';

/**
 * O diário de campo.
 *
 * ── A pergunta que esta tela responde ───────────────────────────────
 *
 * «O que as equipes fizeram ontem?» — e não «o que a OS 2612124 fez
 * ontem». Quem coordena campo pensa por DIA e por EQUIPE; uma tela por
 * ordem de serviço obrigaria a abrir sete fichas para montar a mesma
 * resposta. A ficha da OS também lista os diários dela, para quem chega
 * pelo outro lado.
 *
 * ── Fechar é assinar ────────────────────────────────────────────────
 *
 * O §10 pede «assinaturas ou confirmações». O diário nasce rascunho e se
 * fecha com nome e hora; fechado, não muda mais. Corrigir é REABRIR com
 * motivo — a correção fica registrada e o que foi assinado continua
 * legível. É a restrição 25 aplicada ao registro de campo, que é dado
 * bruto como o da prensa.
 */

const TOM: Record<string, Tom> = {
  rascunho: 'andamento', fechado: 'ok', cancelado: 'critico',
};

const SITUACAO: Record<string, string> = {
  rascunho: 'Rascunho', fechado: 'Fechado', cancelado: 'Cancelado',
};

const CLIMA: Record<string, string> = {
  bom: 'Bom', nublado: 'Nublado', garoa: 'Garoa', chuva_fraca: 'Chuva fraca',
  chuva_forte: 'Chuva forte', vento: 'Vento', calor_forte: 'Calor forte',
  frio: 'Frio', neblina: 'Neblina',
};

const MOTIVO: Record<string, string> = {
  chuva: 'Chuva', quebra_equipamento: 'Quebra de equipamento',
  falta_acesso: 'Falta de acesso', ordem_do_cliente: 'Ordem do cliente',
  acidente: 'Acidente', falta_material: 'Falta de material',
  deslocamento: 'Deslocamento', interferencia: 'Interferência',
  outro: 'Outro',
};

const TIPO_PONTO: Record<string, string> = {
  spt: 'SPT', trado: 'Trado', rotativa: 'Rotativa', mista: 'Mista',
  cpt: 'CPT', cptu: 'CPTu', scptu: 'SCPTu', dmt: 'DMT', sdmt: 'SDMT',
  vane: 'Vane', permeabilidade: 'Permeabilidade', placa: 'Placa',
  poco: 'Poço', instrumento: 'Instrumento', outro: 'Outro',
};

const hhmm = (v: string | null) => (v ? String(v).slice(0, 5) : '—');

// ── a agenda ─────────────────────────────────────────────────────────

export function Diarios() {
  const [linhas, setLinhas] = useState<Diario[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [equipe, setEquipe] = useState<string | null>(null);
  const [situacao, setSituacao] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (equipe) q.set('equipe', equipe);
      if (situacao) q.set('situacao', situacao);
      setLinhas(await verDiarios(q.toString()));
    } catch (e) { setErro(e); }
  }, [equipe, situacao]);

  useEffect(() => { void carregar(); }, [carregar]);
  useEffect(() => {
    verEquipes('campo').then(setEquipes).catch(() => setEquipes([]));
  }, []);

  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Diários de campo</h2>
        <span className="sub">
          {linhas == null ? 'carregando' : `${linhas.length} dia(s)`}
        </span>
      </div>

      <p className="sub">
        O registro do dia: equipe, veículo, tempo, jornada, paradas e o
        avanço de cada furo ou vertical. Abre-se pela ficha da ordem de serviço.
      </p>

      <div className="linha-campos">
        <Escolha
          rotulo="Equipe" valor={equipe} aoEscolher={setEquipe}
          opcoes={equipes.map((e) => ({ valor: e.id, texto: e.codigo,
            detalhe: e.nome }))}
        />
        <Escolha
          rotulo="Situação" valor={situacao} aoEscolher={setSituacao}
          opcoes={Object.entries(SITUACAO).map(([valor, texto]) => ({
            valor, texto }))}
        />
      </div>

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : linhas.length === 0 ? (
          <Vazio>
            Nenhum diário no filtro. O diário nasce na ficha da ordem de
            serviço, onde estão a obra e os pontos daquele dia.
          </Vazio>
        ) : (
          /*
           * Os diários numa tabela com ficha (onda 219): o dia, a obra, a
           * equipe e a situação ficam; jornada e avanço são detalhe e, com
           * a ficha aberta, moram nela — com o tempo, as paradas e quem
           * assinou, que antes só se viam abrindo o diário.
           */
          <TabelaComFicha
            linhas={linhas}
            chave={(d) => d.id}
            nome={(d) => `Diário de ${fmtData(d.data)}`}
            rotulo="Diários de campo"
            colunas={COLUNAS_DO_DIARIO}
            ficha={fichaDoDiario}
          />
        )}
    </div>
  );
}

// ── a ficha do dia ───────────────────────────────────────────────────

export function DiarioFicha() {
  const { id = '' } = useParams();
  const [d, setD] = useState<FichaDoDiario | null>(null);
  /* O elo da OS leva ao Campo DA OS (H1, onda 295), e não à ficha: o diário é
     do Campo, e o sondador não alcança a ficha — ela é do Hub e do LabManager. */
  useCaminho(d ? [
    ...(d.diario.os_id
      ? [{ rotulo: 'OS', codigo: d.diario.os_numero ?? undefined, para: `/os/${d.diario.os_id}/campo` }]
      : []),
    { rotulo: `Diário de ${fmtData(d.diario.data)}` },
  ] : null);
  const [erro, setErro] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [lancando, setLancando] = useState(false);
  const [parando, setParando] = useState(false);
  const [retificando, setRetificando] = useState(false);
  const { pode } = useSessao();

  const carregar = useCallback(async () => {
    setErro(null);
    try { setD(await verDiario(id)); } catch (e) { setErro(e); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Abrindo o diário" />;

  const { diario } = d;
  const aberto = diario.situacao === 'rascunho';
  /* Lançar e fechar o dia é do Campo («Editar» do Campo, D9), e não mais da OS:
     a API pede `campo.editar` desde a H1 (onda 295). */
  const podeEditar = pode('campo.editar') && aberto;

  const fechar = async () => {
    setOcupado(true); setAviso(null);
    try { setD(await fecharDiario(id)); }
    catch (e) { setAviso(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          <h1>Diário de {fmtData(diario.data)}</h1>
          <p className="sub">
            <Link to={`/os/${diario.os_id}/campo`}>{diario.os_numero}</Link>
            {' · '}{diario.obra_codigo} — {diario.obra_nome}
            {diario.equipe_codigo ? ` · ${diario.equipe_codigo}` : ''}
          </p>
        </div>
        <Selo tom={TOM[diario.situacao] ?? 'neutro'}>
          {SITUACAO[diario.situacao] ?? diario.situacao}
        </Selo>
      </div>

      {aviso && <Aviso tipo="erro">{aviso}</Aviso>}

      {diario.situacao === 'fechado' && (
        <Aviso tipo="ok" titulo="Dia assinado">
          Fechado por {diario.fechado_por_nome} em{' '}
          {fmtData(diario.fechado_em)}. Daqui em diante ele não muda: para
          corrigir, reabra com o motivo — a correção fica registrada e o
          que foi assinado continua legível.
        </Aviso>
      )}

      {/* ── 1 · o dia ───────────────────────────────────────────── */}

      <section className="bloco">
        <h2>1 · O dia</h2>
        <dl className="resumo">
          <div><dt>Responsável</dt><dd>{diario.responsavel_nome ?? '—'}</dd></div>
          <div><dt>Equipe</dt><dd>{diario.equipe_nome ?? '—'}</dd></div>
          <div><dt>Veículo</dt><dd>{diario.veiculo_placa ?? '—'}</dd></div>
          <div><dt>Tempo</dt>
            <dd>{diario.clima ? CLIMA[diario.clima] ?? diario.clima : '—'}</dd>
          </div>
          <div><dt>Jornada</dt>
            <dd>{hhmm(diario.hora_inicio)}–{hhmm(diario.hora_fim)}</dd>
          </div>
          <div className="destaque"><dt>Horas úteis</dt>
            <dd>{diario.horas_trabalhadas == null ? '—'
              : `${numero(Number(diario.horas_trabalhadas), 2)} h`}</dd>
          </div>
        </dl>
        <p className="sub">
          Horas úteis = jornada − intervalo − paralisações. A conta é do
          banco, e não desta tela.
        </p>
      </section>

      {/* ── 2 · o avanço ────────────────────────────────────────── */}

      <section className="bloco">
        <h2>
          2 · Avanço por ponto
          <span className="estado-do-bloco">
            {d.pontos.length === 0 ? 'nenhum ponto lançado'
              : `${contarPontos(d.pontos.map((p) => p.ponto_tipo))} · ${
                numero(Number(diario.avanco_m ?? 0), 2)} m`}
          </span>
        </h2>
        {podeEditar && (
          <button type="button" className="botao secundario"
            onClick={() => setLancando(true)}>
            Lançar avanço
          </button>
        )}
        {d.pontos.length === 0 ? (
          <p className="sub">
            Nada lançado ainda. Registre de que profundidade a que
            profundidade cada furo andou — «6 m no SP-03» não diz se foram
            os primeiros seis ou os seis seguintes aos oito de ontem.
          </p>
        ) : (
          <div className="rola">
            <table>
              <thead>
                <tr>
                  <th scope="col">Ponto</th><th scope="col">Tipo</th>
                  <th scope="col" className="n">De</th>
                  <th scope="col" className="n">A</th>
                  <th scope="col" className="n">Avanço</th>
                  <th scope="col" className="n">N.A.</th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {d.pontos.map((p) => (
                  <tr key={p.id}>
                    <th scope="row">{p.ponto_codigo}</th>
                    <td>{TIPO_PONTO[p.ponto_tipo] ?? p.ponto_tipo}</td>
                    <td className="n">{numero(Number(p.profundidade_inicial), 2)}</td>
                    <td className="n">{numero(Number(p.profundidade_final), 2)}</td>
                    <td className="n">{numero(Number(p.avanco_m), 2)} m</td>
                    <td className="n">
                      {p.nivel_agua == null ? '—'
                        : numero(Number(p.nivel_agua), 2)}
                    </td>
                    <td>{p.concluido ? 'concluído' : 'em andamento'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── 3 · as paradas ──────────────────────────────────────── */}

      <section className="bloco">
        <h2>
          3 · Paralisações
          <span className="estado-do-bloco">
            {d.paralisacoes.length === 0 ? 'nenhuma'
              : `${d.paralisacoes.length} parada(s)`}
          </span>
        </h2>
        {podeEditar && (
          <button type="button" className="botao secundario"
            onClick={() => setParando(true)}>
            Registrar paralisação
          </button>
        )}
        {d.paralisacoes.length === 0 ? (
          <p className="sub">
            Nenhuma parada registrada. Um dia inteiro parado é um dia COM
            paralisação, não um dia vazio — e o motivo vira indicador
            (§16).
          </p>
        ) : (
          <ul className="lista-simples">
            {d.paralisacoes.map((p) => (
              <li key={p.id}>
                <b>{hhmm(p.inicio)}–{hhmm(p.fim)}</b>{' '}
                {MOTIVO[p.motivo] ?? p.motivo}
                {p.descricao ? ` — ${p.descricao}` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── 4 · as fotos do dia ─────────────────────────────────── */}

      <FotosDoRegistro
        id={id}
        alvo="diario"
        numero={4}
        titulo="Fotografias do dia"
        dica="A frente de serviço, o acesso, o que parou o trabalho, o
              equipamento montado. Elas saem no diário em papel."
        podeLancar={podeEditar}
      />

      {/* ── 5 · ocorrências ─────────────────────────────────────── */}

      {diario.ocorrencias && (
        <section className="bloco">
          <h2>5 · Ocorrências</h2>
          <p style={{ whiteSpace: 'pre-wrap' }}>{diario.ocorrencias}</p>
        </section>
      )}

      {/* ── 6 · o papel ─────────────────────────────────────────── */}

      <section className="bloco laudo">
        <h2>{diario.ocorrencias ? '6' : '5'} · Diário em papel</h2>
        <AcoesDoLaudo ensaioId={id} nome="diario"
          caminho={`/diarios/${id}/documento`} />
        <p className="sub">
          O rascunho sai com marca d&apos;água RASCUNHO — um dia em
          digitação impresso e limpo é o caminho mais curto para alguém
          levar à reunião um dia que ninguém assinou.
        </p>

        {podeEditar && (
          <div className="acoes">
            <button type="button" className="principal" disabled={ocupado}
              onClick={() => { void fechar(); }}>
              {ocupado ? 'Fechando…' : 'Fechar e assinar o dia'}
            </button>
          </div>
        )}
        {diario.situacao === 'fechado' && pode('campo.editar') && (
          <div className="acoes">
            <button type="button" onClick={() => setRetificando(true)}>
              Reabrir para corrigir
            </button>
          </div>
        )}
      </section>

      {lancando && (
        <DialogoAvanco d={d} aoFechar={() => setLancando(false)}
          aoPronto={(nova) => { setD(nova); setLancando(false); }} />
      )}
      {parando && (
        <DialogoParalisacao id={id} aoFechar={() => setParando(false)}
          aoPronto={(nova) => { setD(nova); setParando(false); }} />
      )}
      {retificando && (
        <DialogoRetificar id={id} aoFechar={() => setRetificando(false)}
          aoPronto={(nova) => { setD(nova); setRetificando(false); }} />
      )}
    </div>
  );
}

function DialogoAvanco({ d, aoFechar, aoPronto }: {
  d: FichaDoDiario; aoFechar(): void; aoPronto(n: FichaDoDiario): void;
}) {
  const [ponto, setPonto] = useState<string | null>(null);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [na, setNa] = useState('');
  const [concluido, setConcluido] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  /*
   * Escolher o furo PREENCHE o «de» com a profundidade em que ele parou.
   *
   * Sem isto, quem lança teria de lembrar de cor onde o SP-03 ficou
   * ontem — e é assim que um dia começa em zero num furo que já andou
   * oito metros.
   */
  const escolhido = d.disponiveis.find((x) => x.ponto_id === ponto);
  const escolher = (v: string | null) => {
    setPonto(v);
    const p = d.disponiveis.find((x) => x.ponto_id === v);
    if (p) setDe(String(Number(p.profundidade_lancada)).replace('.', ','));
  };

  const gravar = async () => {
    if (!ponto) return;
    setOcupado(true); setErro(null);
    try {
      aoPronto(await lancarPontoDoDia(d.diario.id, {
        pontoId: ponto,
        profundidadeInicial: paraNumero(de) ?? 0,
        profundidadeFinal: paraNumero(ate) ?? 0,
        nivelAgua: paraNumero(na) ?? undefined,
        concluido,
      }));
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Avanço do dia" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {d.disponiveis.length === 0 ? (
        <Vazio>
          Esta ordem de serviço ainda não tem ponto cadastrado. Cadastre-os
          no Campo da OS — o avanço pende do ponto, não do texto.
        </Vazio>
      ) : (
        <>
          <Escolha rotulo="Ponto" valor={ponto} aoEscolher={escolher}
            opcoes={d.disponiveis.map((p) => ({
              valor: p.ponto_id, texto: p.codigo,
              detalhe: `em ${numero(Number(p.profundidade_lancada), 2)} m`,
            }))} />
          <div className="linha-campos">
            <Campo rotulo="De (m)" value={de} inputMode="decimal"
              dica={`A profundidade em que ${artigoDoPonto(escolhido?.tipo)} estava`}
              onChange={(ev) => setDe(ev.target.value)} />
            <Campo rotulo="A (m)" value={ate} inputMode="decimal"
              onChange={(ev) => setAte(ev.target.value)} />
          </div>
          <Campo rotulo="Nível d'água (m)" value={na} inputMode="decimal"
            onChange={(ev) => setNa(ev.target.value)} />
          <label className="campo em-linha">
            <input type="checkbox" checked={concluido}
              onChange={(ev) => setConcluido(ev.target.checked)} />
            <span>{concluidoNoDia(escolhido?.tipo)}</span>
          </label>
        </>
      )}
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="principal"
          disabled={ocupado || !ponto || !ate}
          onClick={() => { void gravar(); }}>
          {ocupado ? 'Gravando…' : 'Lançar'}
        </button>
      </div>
    </Dialogo>
  );
}

function DialogoParalisacao({ id, aoFechar, aoPronto }: {
  id: string; aoFechar(): void; aoPronto(n: FichaDoDiario): void;
}) {
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [motivo, setMotivo] = useState<string | null>('chuva');
  const [descricao, setDescricao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const gravar = async () => {
    if (!motivo || !inicio) return;
    setOcupado(true); setErro(null);
    try {
      aoPronto(await lancarParalisacao(id, {
        inicio, fim: fim || undefined, motivo,
        descricao: descricao.trim() || undefined,
      }));
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Paralisação" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <div className="linha-campos">
        <Campo rotulo="Início" type="time" value={inicio}
          onChange={(ev) => setInicio(ev.target.value)} />
        <Campo rotulo="Fim" type="time" value={fim}
          onChange={(ev) => setFim(ev.target.value)} />
      </div>
      <Escolha rotulo="Motivo" valor={motivo} limpavel={false}
        aoEscolher={setMotivo}
        opcoes={Object.entries(MOTIVO).map(([valor, texto]) => ({
          valor, texto }))} />
      <label className="campo">
        <span className="rotulo">
          Descrição{motivo === 'outro' ? '' : ' (opcional)'}
        </span>
        <textarea rows={2} value={descricao}
          onChange={(ev) => setDescricao(ev.target.value)} />
      </label>
      <p className="sub">
        O motivo vira indicador (§16). «Outro» sem descrição ocupa o lugar
        de uma causa que ninguém recupera depois.
      </p>
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="principal"
          disabled={ocupado || !inicio || !motivo
            || (motivo === 'outro' && descricao.trim().length < 3)}
          onClick={() => { void gravar(); }}>
          {ocupado ? 'Gravando…' : 'Registrar'}
        </button>
      </div>
    </Dialogo>
  );
}

function DialogoRetificar({ id, aoFechar, aoPronto }: {
  id: string; aoFechar(): void; aoPronto(n: FichaDoDiario): void;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try { aoPronto(await reabrirDiario(id, motivo)); }
    catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Reabrir o diário" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <p className="sub">
        O que foi assinado continua legível: a reabertura fica registrada
        com o seu nome e com o motivo.
      </p>
      <label className="campo">
        <span className="rotulo">Por quê?</span>
        <textarea rows={3} value={motivo}
          onChange={(ev) => setMotivo(ev.target.value)}
          placeholder="a profundidade final do SP-03 foi digitada errada…" />
      </label>
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="principal"
          disabled={ocupado || motivo.trim().length < 10}
          onClick={() => { void gravar(); }}>
          {ocupado ? 'Gravando…' : 'Reabrir'}
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * Abrir o dia — a partir da ficha da ordem de serviço.
 *
 * Nasce lá porque é lá que estão a obra, a equipe e os furos daquele
 * dia. Abrir um diário de uma tela geral obrigaria a escolher a OS no
 * escuro.
 */
export function FormNovoDiario({ osId, aoFechar, aoPronto }: {
  osId: string; aoFechar(): void; aoPronto(id: string): void;
}) {
  const [dia, setDia] = useState(new Date().toLocaleDateString('en-CA'));
  const [equipe, setEquipe] = useState<string | null>(null);
  const [responsavel, setResponsavel] = useState<string | null>(null);
  const [gente, setGente] = useState<{ id: string; nome: string }[]>([]);
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    verEquipes('campo').then(setEquipes).catch(() => setEquipes([]));
    api<{ id: string; nome: string }[]>('/funcionarios')
      .then(setGente).catch(() => setGente([]));
  }, []);

  const gravar = async () => {
    if (!responsavel) return;
    setOcupado(true); setErro(null);
    try {
      const r = await criarDiario(osId, {
        data: dia, equipeId: equipe ?? undefined, responsavelId: responsavel,
      });
      aoPronto(r.diario.id);
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Abrir diário de campo" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <Campo rotulo="Dia" type="date" value={dia}
        onChange={(ev) => setDia(ev.target.value)} />
      <Escolha rotulo="Equipe" valor={equipe} aoEscolher={setEquipe}
        opcoes={equipes.map((e) => ({ valor: e.id, texto: e.codigo,
          detalhe: e.nome }))} />
      <Escolha rotulo="Responsável pelo dia" valor={responsavel}
        aoEscolher={setResponsavel}
        opcoes={gente.map((g) => ({ valor: g.id, texto: g.nome }))} />
      <p className="sub">
        O responsável é quem estava lá — não quem digita, que a auditoria
        já registra.
      </p>
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="principal"
          disabled={ocupado || !responsavel}
          onClick={() => { void gravar(); }}>
          {ocupado ? 'Abrindo…' : 'Abrir'}
        </button>
      </div>
    </Dialogo>
  );
}

/** A jornada do dia: início–fim e as horas úteis. */
function Jornada({ d }: { d: Diario }) {
  return (
    <>
      {hhmm(d.hora_inicio)}–{hhmm(d.hora_fim)}
      {d.horas_trabalhadas != null && (
        <span className="sub">{numero(Number(d.horas_trabalhadas), 2)} h úteis</span>
      )}
    </>
  );
}

/** O avanço em metros e as paradas do dia. */
function Avanco({ d }: { d: Diario }) {
  return (
    <>
      {d.avanco_m == null ? <span className="nada">—</span> : `${numero(Number(d.avanco_m), 2)} m`}
      {Number(d.paralisacoes) > 0 && <span className="sub">{d.paralisacoes} parada(s)</span>}
    </>
  );
}

const COLUNAS_DO_DIARIO: ColunaComFicha<Diario>[] = [
  {
    chave: 'dia',
    cabeca: <th scope="col">Dia</th>,
    celula: (d) => (
      <th scope="row">
        <Link to={`/campo/diario/${d.id}`}>{fmtData(d.data)}</Link>
        {d.os_numero && <span className="sub">{d.os_numero}</span>}
      </th>
    ),
  },
  {
    chave: 'obra',
    cabeca: <th scope="col">Obra</th>,
    celula: (d) => (
      <td>
        {d.obra_codigo}
        {d.obra_nome && <span className="sub">{d.obra_nome}</span>}
      </td>
    ),
  },
  {
    chave: 'equipe',
    cabeca: <th scope="col">Equipe</th>,
    celula: (d) => (
      <td>
        {d.equipe_codigo ?? <span className="nada">—</span>}
        {d.veiculo_placa && <span className="sub mono">{d.veiculo_placa}</span>}
      </td>
    ),
  },
  {
    chave: 'jornada', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">Jornada</th>,
    celula: (d) => <td className="col-opcional"><Jornada d={d} /></td>,
  },
  {
    chave: 'avanco', detalhe: true,
    cabeca: <th scope="col">Avanço</th>,
    celula: (d) => <td className="n"><Avanco d={d} /></td>,
  },
  {
    chave: 'situacao',
    cabeca: <th scope="col">Situação</th>,
    celula: (d) => (
      <td>
        <Selo tom={TOM[d.situacao] ?? 'neutro'}>{SITUACAO[d.situacao] ?? d.situacao}</Selo>
      </td>
    ),
  },
];

/** A ficha do dia: o que as colunas recolhidas diziam, o tempo e quem assinou. */
function fichaDoDiario(d: Diario): FichaDaLinha {
  return {
    rotulo: 'Diário de campo',
    titulo: fmtData(d.data),
    tituloCodigo: true,
    sub: (
      <>
        <span>{[d.obra_codigo, d.obra_nome].filter(Boolean).join(' · ')}</span>
        {d.os_numero && <span>OS {d.os_numero}</span>}
      </>
    ),
    corpo: (
      <>
        <div className="tf-selos">
          <Selo tom={TOM[d.situacao] ?? 'neutro'}>{SITUACAO[d.situacao] ?? d.situacao}</Selo>
        </div>
        {d.situacao === 'cancelado' && d.motivo_cancelamento && (
          <div className="tf-aviso critico">
            <strong>Cancelado.</strong> {d.motivo_cancelamento}
          </div>
        )}
        <CamposDaFicha itens={[
          ['Equipe', [d.equipe_codigo, d.equipe_nome].filter(Boolean).join(' · ')],
          ['Veículo', d.veiculo_placa && <span className="mono">{d.veiculo_placa}</span>],
          ['Responsável', d.responsavel_nome],
          ['Tempo', [d.clima ? CLIMA[d.clima] ?? d.clima : null,
            d.temperatura_c != null ? `${numero(Number(d.temperatura_c), 0)} °C` : null]
            .filter(Boolean).join(' · ')],
          ['Jornada', <Jornada d={d} />],
          ['Avanço', <Avanco d={d} />],
          ['Pontos no dia', Number(d.pontos_no_dia) > 0 ? d.pontos_no_dia : null],
          ['Ocorrências', d.ocorrencias],
          ['Fechado', d.fechado_em
            ? `${fmtData(d.fechado_em)}${d.fechado_por_nome ? ` · ${d.fechado_por_nome}` : ''}`
            : null],
        ]} />
      </>
    ),
    acoes: <Link className="botao" to={`/campo/diario/${d.id}`}>Abrir diário</Link>,
  };
}

/** «o furo» / «a vertical» / «o ponto» — o artigo acompanha o nome do tipo (R12, onda 312). */
function artigoDoPonto(tipo: string | null | undefined): string {
  const nome = nomeDoPonto(tipo);
  return nome === 'vertical' ? 'a vertical' : `o ${nome}`;
}

/** A frase da caixa «concluído», com o nome e o gênero do tipo do ponto escolhido. */
function concluidoNoDia(tipo: string | null | undefined): string {
  const nome = nomeDoPonto(tipo);
  return nome === 'vertical' ? 'A vertical foi concluída neste dia' : `O ${nome} foi concluído neste dia`;
}
